import React, { useState, useEffect } from 'react';
import { bulkAPI, API_BASE_URL } from '../../services/api';
import BulkCardMedia from '../../components/BulkCardMedia';
import { ProductListSkeleton } from '../../components/skeletons';
import { renderIcon } from '../admin/TaxonomyManager';

const API_BASE = API_BASE_URL;

const cleanIcon = (icon) => (icon && typeof icon === 'string' && !icon.startsWith('http') ? `${icon} ` : '');

const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url}`;
};

export default function BulkBrowse() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bidAmounts, setBidAmounts] = useState({});
  const [bidQtys, setBidQtys] = useState({});
  const [bidLoading, setBidLoading] = useState(null);
  const [bidSuccess, setBidSuccess] = useState(null);
  const [detail, setDetail] = useState(null);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [myBids, setMyBids] = useState([]);
  const [tab, setTab] = useState('browse'); // 'browse' or 'mybids'
  const [bidErrors, setBidErrors] = useState({});
  const [showContactMap, setShowContactMap] = useState({});

  const loadAll = () => {
    bulkAPI.listListings({ limit: 50 })
      .then(r => setListings(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    bulkAPI.myBids()
      .then(r => setMyBids(r.data.data || []))
      .catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);
  
  const updateBid = (id, field, val) => {
    const listing = listings.find(l => l._id === id) || detail;
    if (!listing) return;

    const nextPrice = field === 'price' ? val : (bidAmounts[id] || '');
    const nextQty = field === 'qty' ? val : (bidQtys[id] || '1');

    if (field === 'price') setBidAmounts(p => ({ ...p, [id]: val }));
    else setBidQtys(p => ({ ...p, [id]: val }));

    let err = '';
    const p = Number(nextPrice);
    const q = Number(nextQty);
    const available = listing.stock - (listing.sold || 0);

    if (nextPrice && p > listing.priceCeiling) err = `Max price is ₹${listing.priceCeiling.toLocaleString('en-IN')}`;
    else if (nextPrice && p > 0 && p < listing.priceFloor) err = `Min price is ₹${listing.priceFloor.toLocaleString('en-IN')}`;
    else if (nextQty && q > available) err = `Only ${available} units available`;
    else if (nextQty && q <= 0) err = `Quantity must be at least 1`;

    setBidErrors(p => ({ ...p, [id]: err }));
  };

  const handleBid = async (listingId) => {
    const listing = listings.find(l => l._id === listingId) || detail;
    if (!listing) return;

    const priceStr = bidAmounts[listingId] || '';
    const qtyStr = bidQtys[listingId] || '1';
    const price = Number(priceStr);
    const quantity = Number(qtyStr);

    // Final validation before submit
    let err = '';
    const available = listing.stock - (listing.sold || 0);

    if (!price || price <= 0) err = 'Enter a valid price';
    else if (price < listing.priceFloor || price > listing.priceCeiling) {
      err = `Price must be ₹${listing.priceFloor.toLocaleString('en-IN')} - ₹${listing.priceCeiling.toLocaleString('en-IN')}`;
    } else if (quantity > available) {
      err = `Only ${available} available`;
    } else if (quantity <= 0) {
      err = `Enter valid quantity`;
    }

    if (err) {
      setBidErrors(p => ({ ...p, [listingId]: err }));
      return;
    }

    setBidLoading(listingId);
    try {
      await bulkAPI.placeBid({ bulkListingId: listingId, bidPrice: price, qty: quantity });
      setBidSuccess(listingId);
      setBidAmounts(p => ({ ...p, [listingId]: '' }));
      setBidQtys(p => ({ ...p, [listingId]: '' }));
      setBidErrors(p => ({ ...p, [listingId]: '' }));
      bulkAPI.myBids().then(r => setMyBids(r.data.data || [])).catch(() => { });
      setTimeout(() => setBidSuccess(null), 3000);
    } catch (err) {
      setBidErrors(p => ({ ...p, [listingId]: err.response?.data?.message || 'Failed to place bid' }));
    } finally { setBidLoading(null); }
  };

  const compileSearchRegex = (s) => {
    if (!s) return null;
    const stripped = s.trim().replace(/\s+/g, '');
    if (!stripped) return null;
    const chars = [...stripped].map(char => char.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'));
    return new RegExp(chars.join('[\\s\\-_]*'), 'i');
  };

  const searchRegex = compileSearchRegex(search);

  const filtered = searchRegex
    ? listings.filter(l =>
        searchRegex.test(l.title || '') ||
        searchRegex.test(l.categoryId?.name || '') ||
        searchRegex.test(l.brandId?.name || ''))
    : listings;

  if (loading) {
    return (
      <div style={{ padding: 24, background: 'var(--cream)', minHeight: '100vh' }}>
        <ProductListSkeleton count={6} columns={3} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: 'var(--cream)', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)' }}>Bulk Deals</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>Name your price and bid</div>
        </div>
        {tab === 'browse' && (
          <input placeholder="Search products, brands..." value={search} onChange={e => setSearch(e.target.value)}
            className="form-input" style={{ maxWidth: 280 }} />
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTab('browse')}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: tab === 'browse' ? 'var(--green)' : '#fff', color: tab === 'browse' ? '#fff' : 'var(--char)', border: '1px solid var(--border)' }}>
          📦 Browse Deals ({filtered.length})
        </button>
        <button onClick={() => setTab('mybids')}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: tab === 'mybids' ? 'var(--green)' : '#fff', color: tab === 'mybids' ? '#fff' : 'var(--char)', border: '1px solid var(--border)' }}>
          🏷️ My Bids ({myBids.length})
        </button>
      </div>

      {/* My Bids Tab */}
      {tab === 'mybids' && (
        myBids.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏷️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No bids yet</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Browse deals and place your first bid!</div>
            <button onClick={() => setTab('browse')} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, background: 'var(--green)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Browse Deals</button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {myBids.map(b => {
              const l = b.bulkListingId;
              const defMedia = l?.media?.find(m => m.isDefault) || l?.media?.[0];
              const statusColor = { pending: { bg: '#FFF8E7', text: '#F5A623' }, accepted: { bg: '#E8F5EE', text: '#1B5C3A' }, rejected: { bg: '#FEE2E2', text: '#DC2626' } };
              const sc = statusColor[b.status] || statusColor.pending;
              return (
                <div key={b._id} style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border)', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    {/* Thumbnail */}
                    <div style={{ width: 70, height: 70, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: '#F3F4F6' }}>
                      {defMedia ? (
                        defMedia.type === 'video'
                          ? <video src={getMediaUrl(defMedia.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                          : <img src={getMediaUrl(defMedia.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>📦</div>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--char)', marginBottom: 4 }}>{l?.title || 'Listing removed'}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                        {l?.categoryId?.name && <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: '#E8F5EE', color: '#1B5C3A' }}>{l.categoryId.name}</span>}
                        {l?.subCategoryId?.name && <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: '#EEF2FF', color: '#4338CA' }}>{l.subCategoryId.name}</span>}
                        {l?.brandId?.name && <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 6, background: '#FFF8E7', color: '#92400E' }}>{l.brandId.name}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {b.status === 'accepted' ? `by ${l?.vendorId?.fullName || 'Vendor'}` : 'by Vendor'} · {new Date(b.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </div>
                    </div>

                    {/* Bid details */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, fontWeight: 700, color: 'var(--green)' }}>₹{(b.bidPrice || 0).toLocaleString('en-IN')}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>Qty: {b.qty || 1}</div>
                      <span style={{ display: 'inline-block', marginTop: 4, fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 8, background: sc.bg, color: sc.text, textTransform: 'capitalize' }}>
                        {b.status}
                      </span>
                    </div>
                  </div>

                  {/* Contact Vendor section (only if status is accepted) */}
                  {b.status === 'accepted' && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 2 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>Deal Accepted! 🎉</span>
                        <button
                          onClick={() => setShowContactMap(prev => ({ ...prev, [b._id]: !prev[b._id] }))}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 8,
                            border: '1px solid var(--green)',
                            background: showContactMap[b._id] ? 'var(--green)' : '#fff',
                            color: showContactMap[b._id] ? '#fff' : 'var(--green)',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          {showContactMap[b._id] ? 'Hide Contact' : 'Contact Vendor'}
                        </button>
                      </div>

                      {showContactMap[b._id] && (
                        <div style={{
                          marginTop: 10,
                          padding: 12,
                          background: 'var(--cream)',
                          borderRadius: 8,
                          border: '1px solid var(--border)',
                          fontSize: 13,
                          color: 'var(--char)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6
                        }}>
                          <div><strong>Vendor Name:</strong> {l?.vendorId?.fullName || 'N/A'}</div>
                          <div><strong>Email:</strong> <a href={`mailto:${l?.vendorId?.email}`} style={{ color: 'var(--green)', textDecoration: 'none', fontWeight: 600 }}>{l?.vendorId?.email || 'N/A'}</a></div>
                          <div><strong>Phone Number:</strong> <a href={`tel:${l?.vendorId?.mobileNumber}`} style={{ color: 'var(--green)', textDecoration: 'none', fontWeight: 600 }}>{l?.vendorId?.mobileNumber || 'N/A'}</a></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Browse Tab */}
      {tab === 'browse' && (filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 12, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>{search ? 'No matching listings' : 'No bulk listings yet'}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Check back later for new deals.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filtered.map(l => {
            const defMedia = l.media?.find(m => m.isDefault) || l.media?.[0];
            return (
              <div key={l._id} style={{ background: '#fff', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {/* Interactive Media Slider */}
                <BulkCardMedia 
                  listing={l} 
                  height={180} 
                  getMediaUrl={getMediaUrl} 
                  onDetailClick={() => { setDetail(l); setGalleryIdx(0); }} 
                />


                <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div onClick={() => { setDetail(l); setGalleryIdx(0); }}
                    style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)', marginBottom: 6, cursor: 'pointer' }}>{l.title}</div>

                  {/* Badges */}
                  <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
                    {l.categoryId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#E8F5EE', color: '#1B5C3A' }}>{cleanIcon(l.categoryId.icon)}{l.categoryId.name}</span>}
                    {l.subCategoryId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#EEF2FF', color: '#4338CA' }}>{l.subCategoryId.name}</span>}
                    {l.brandId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#FFF8E7', color: '#92400E', display: 'inline-flex', alignItems: 'center' }}>{renderIcon(l.brandId.icon, { width: 14, height: 14, marginRight: 4 })}{l.brandId.name}</span>}
                  </div>

                  {l.description && <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 10 }}>{l.description.length > 80 ? l.description.slice(0, 80) + '...' : l.description}</p>}

                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green)', marginBottom: 10 }}>
                    ₹{l.priceFloor?.toLocaleString('en-IN')} - ₹{l.priceCeiling?.toLocaleString('en-IN')}
                    <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--muted)', marginLeft: 4 }}>/ unit</span>
                  </div>

                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8, background: '#F3F4F6', color: 'var(--char)' }}>Stock: {l.stock - (l.sold || 0)}</span>
                  </div>

                  {/* Bid */}
                  <div style={{ marginTop: 'auto', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    {bidSuccess === l._id ? (
                      <div style={{ background: '#E8F5EE', borderRadius: 8, padding: 10, fontSize: 13, color: '#1B5C3A', fontWeight: 600, textAlign: 'center' }}>✅ Bid placed!</div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input type="number" placeholder="₹ Price" value={bidAmounts[l._id] || ''}
                          onChange={e => updateBid(l._id, 'price', e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className="form-input" style={{ flex: 1, padding: '8px 10px', borderColor: bidErrors[l._id] ? '#dc2626' : 'var(--border)' }} />
                        <input type="number" placeholder="Qty" value={bidQtys[l._id] || ''}
                          onChange={e => updateBid(l._id, 'qty', e.target.value)}
                          onWheel={(e) => e.target.blur()}
                          className="form-input" style={{ width: 55, padding: '8px 10px', borderColor: bidErrors[l._id] ? '#dc2626' : 'var(--border)' }} />
                        <button onClick={() => handleBid(l._id)} disabled={bidLoading === l._id}
                          style={{ background: 'var(--green)', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {bidLoading === l._id ? '...' : 'Bid'}
                        </button>
                      </div>
                    )}
                    {bidErrors[l._id] && <div style={{ color: '#dc2626', fontSize: 10, marginTop: 4, fontWeight: 600 }}>{bidErrors[l._id]}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Detail Modal */}
      {detail && (() => {
        const media = detail.media || [];
        const current = media[galleryIdx];
        return (
          <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 660, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
              {/* Gallery */}
              {media.length > 0 ? (
                <div style={{ position: 'relative', background: '#111', borderRadius: '16px 16px 0 0', overflow: 'hidden' }}>
                  <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {current?.type === 'video'
                      ? <video src={getMediaUrl(current.url)} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%' }} />
                      : current ? <img src={getMediaUrl(current.url)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : null}
                  </div>
                  {media.length > 1 && <>
                    <button onClick={() => setGalleryIdx(i => (i - 1 + media.length) % media.length)}
                      style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, fontSize: 18, cursor: 'pointer' }}>‹</button>
                    <button onClick={() => setGalleryIdx(i => (i + 1) % media.length)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, fontSize: 18, cursor: 'pointer' }}>›</button>
                  </>}
                  <span style={{ position: 'absolute', bottom: 8, right: 12, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 8 }}>{galleryIdx + 1} / {media.length}</span>
                  <button onClick={() => setDetail(null)} style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 30, height: 30, fontSize: 15, cursor: 'pointer' }}>✕</button>
                </div>
              ) : (
                <div style={{ height: 180, background: '#F3F4F6', borderRadius: '16px 16px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56 }}>📦</div>
              )}
              {media.length > 1 && (
                <div style={{ display: 'flex', gap: 5, padding: '8px 16px', overflowX: 'auto', background: '#fafafa' }}>
                  {media.map((m, i) => (
                    <div key={i} onClick={() => setGalleryIdx(i)}
                      style={{ width: 50, height: 50, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', border: i === galleryIdx ? '2px solid var(--green)' : '1px solid var(--border)', flexShrink: 0, opacity: i === galleryIdx ? 1 : 0.5 }}>
                      {m.type === 'video'
                        ? <video src={getMediaUrl(m.url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                        : <img src={getMediaUrl(m.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ padding: '16px 20px' }}>
                <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: 'var(--char)', margin: '0 0 8px' }}>{detail.title}</h2>
                <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
                  {detail.categoryId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#E8F5EE', color: '#1B5C3A' }}>{cleanIcon(detail.categoryId.icon)}{detail.categoryId.name}</span>}
                  {detail.subCategoryId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#EEF2FF', color: '#4338CA' }}>{detail.subCategoryId.name}</span>}
                  {detail.brandId?.name && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#FFF8E7', color: '#92400E', display: 'inline-flex', alignItems: 'center' }}>{renderIcon(detail.brandId.icon, { width: 14, height: 14, marginRight: 4 })}{detail.brandId.name}</span>}
                </div>
                {detail.description && <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 14 }}>{detail.description}</p>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 14 }}>
                  <div style={{ background: '#F0FDF4', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #DCFCE7' }}>
                    <div style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>Expected Price Range (per unit)</div>
                    <div style={{ fontWeight: 800, fontSize: 20, color: '#1B5C3A', marginTop: 4 }}>
                      ₹{detail.priceFloor?.toLocaleString('en-IN')} - ₹{detail.priceCeiling?.toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div style={{ background: '#F9FAFB', borderRadius: 10, padding: 10, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>Available</div>
                    <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>{detail.stock - (detail.sold || 0)} units</div>
                  </div>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  {bidSuccess === detail._id ? (
                    <div style={{ background: '#E8F5EE', borderRadius: 8, padding: 12, fontSize: 13, color: '#1B5C3A', fontWeight: 600, textAlign: 'center' }}>✅ Bid placed!</div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="number" placeholder="₹ Your price" value={bidAmounts[detail._id] || ''}
                        onChange={e => updateBid(detail._id, 'price', e.target.value)}
                        className="form-input" style={{ flex: 1, borderColor: bidErrors[detail._id] ? '#dc2626' : 'var(--border)' }} />
                      <input type="number" placeholder="Qty" value={bidQtys[detail._id] || ''}
                        onChange={e => updateBid(detail._id, 'qty', e.target.value)}
                        className="form-input" style={{ width: 70, borderColor: bidErrors[detail._id] ? '#dc2626' : 'var(--border)' }} />
                      <button onClick={() => handleBid(detail._id)} disabled={bidLoading === detail._id}
                        style={{ background: 'var(--green)', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        {bidLoading === detail._id ? '...' : 'Place Bid'}
                      </button>
                    </div>
                  )}
                  {bidErrors[detail._id] && <div style={{ color: '#dc2626', fontSize: 11, marginTop: 6, fontWeight: 600 }}>⚠️ {bidErrors[detail._id]}</div>}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
